import { useState } from 'react'
import type { z } from 'zod'
import type { MonitoringSessionsSchema } from '../schemas/sessionMonitoring'
import { ExternalLink } from 'lucide-react'
import { useCreateProjectUserSessionJourneyMutation } from '../services/projectDataApi'
import { Link } from './nav'
import ScreenshotViewer from './ScreenshotViewer'
import './DailyMonitoringSessions.css'

export default function MonitoringSessionList({ projectId, runId, sessions, canEdit, onRefresh, readOnly = false }: {
  projectId: string; runId?: string; sessions: z.infer<typeof MonitoringSessionsSchema>['sessions']; canEdit: boolean; readOnly?: boolean; onRefresh: () => Promise<unknown>
}) {
  const [createJourney] = useCreateProjectUserSessionJourneyMutation()
  const [workingSession, setWorkingSession] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [mergeSightmap, setMergeSightmap] = useState(false)

  async function requestJourney(sessionId: string, regenerate: boolean) {
    setWorkingSession(sessionId)
    setActionError(null)
    try {
      await createJourney({ projectId, sessionId, regenerate, mergeSightmap }).unwrap()
      await onRefresh()
    } catch {
      setActionError('Could not start journey creation. Please try again.')
    } finally {
      setWorkingSession(null)
    }
  }

  return <>
    {canEdit && !readOnly && <label className="daily-monitoring-sessions__sightmap">
      <input type="checkbox" checked={mergeSightmap} onChange={event => setMergeSightmap(event.target.checked)} />
      Update the project Sightmap when creating a reproduction journey
    </label>}
    {actionError && <p role="alert">{actionError}</p>}
    {sessions.map(session => <article key={session.session_id} className="daily-monitoring-sessions__session">
      <header>
        <div>
          <h3>{session.user_email || 'Anonymous user'}</h3>
          <time dateTime={session.first_received_at}>{new Date(session.first_received_at).toLocaleString(undefined, { timeZoneName: 'short' })}</time>
        </div>
        {!readOnly && <div className="daily-monitoring-sessions__links">
          <Link to={`/projects/${projectId}/user-sessions/${encodeURIComponent(session.session_id)}`}>Session details</Link>
          <a href={session.external_session_url} target="_blank" rel="noreferrer">FullStory <ExternalLink size={13} aria-hidden /></a>
        </div>}
      </header>
      <p className="daily-monitoring-sessions__metadata"><span>{session.interaction_count === null ? 'Interactions unavailable' : `${session.interaction_count.toLocaleString()} interactions`}</span><span className={`daily-monitoring-sessions__badge${session.reviews.some(review => review.output) ? ' daily-monitoring-sessions__badge--reviewed' : ''}`}>{session.reviews.some(review => review.output) ? 'Reviewed' : session.reviews.some(review => ['running', 'queued'].includes(review.latest_status)) ? 'Review pending' : 'Not reviewed'}</span></p>
      {session.reviews.map(review => <details className="daily-monitoring-sessions__review" key={review.id}>
        <summary>
          <strong>{review.reviewer}</strong>
          <span>{review.output ? `${review.output.observations.length} observations` : review.status}</span>
          {review.latest_id !== review.id && <span>Latest attempt: {review.latest_status}</span>}
        </summary>
        <div className="daily-monitoring-sessions__review-body">
          <Link to={`/projects/${projectId}/session-reviewers/${review.reviewer}`}>View reviewer</Link>
          {review.latest_id !== review.id && review.output && <p>Showing the previous saved result. Latest attempt: {review.latest_status}.</p>}
          {!review.output && <p>Review {review.status}. No saved result is available.</p>}
          {review.output?.observations.length === 0 && <p>Review completed with no observations.</p>}
          {review.output?.observations.map((observation, index) => <div className="daily-monitoring-sessions__observation" key={`${observation.code}:${index}`}>
            <h4>{observation.code}</h4>
            <p>{observation.description}</p>
            {Object.keys(observation.attributes).length > 0 && <dl>{Object.entries(observation.attributes).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>}
          </div>)}
          {review.output?.screenshot && <figure>
            <ScreenshotViewer frame={review.output.screenshot.image.url} alt={review.output.screenshot.caption} />
            <figcaption>{review.output.screenshot.caption} · {(review.output.screenshot.timestamp_ms / 1000).toFixed(3)}s into the session</figcaption>
          </figure>}
          {review.journey_id && <Link to={`/projects/${projectId}/journeys/${review.journey_id}${runId ? `?run=${encodeURIComponent(runId)}` : ''}`}>Review reproduction journey</Link>}
        </div>
      </details>)}
      <div className="daily-monitoring-sessions__links">
        {session.journey_id && <Link to={`/projects/${projectId}/journeys/${session.journey_id}${runId ? `?run=${encodeURIComponent(runId)}` : ''}`}>Reproduction journey</Link>}
        {canEdit && !readOnly && <button type="button" disabled={workingSession !== null || session.journey_status === 'inspecting_session' || session.journey_status === 'authoring_journey'} onClick={() => void requestJourney(session.session_id, Boolean(session.journey_id))}>
          {workingSession === session.session_id ? 'Starting journey…' : session.journey_status === 'inspecting_session' || session.journey_status === 'authoring_journey' ? 'Creating journey…' : session.journey_id ? 'Retry creating journey' : 'Create reproduction journey'}
        </button>}
        {session.journey_status && <span>Journey: {session.journey_status.replace(/_/g, ' ')}</span>}
      </div>
    </article>)}
  </>
}
